/**
 * Screen 1b — the course, playing.
 *
 * Full screen, outside the Shell: the course is the page, and a rail with
 * "Bundles" and "People" in it beside a lesson somebody is trying to read is
 * two navigations arguing.
 *
 * ── The frame ───────────────────────────────────────────────────────────────
 * `sandbox="allow-scripts allow-forms allow-popups"` and deliberately **not**
 * `allow-same-origin`: the course is author-supplied HTML, it runs on
 * `player.mauvely.com`, and the pair of those is what keeps it out of this
 * document and out of the session. Everything it tells us arrives by
 * `postMessage` and is validated by window handle — see `player/bus.ts`.
 *
 * The `src` is a play URL with a signed ticket in its path. It goes in the
 * frame and nowhere else: not a link, not a title, not a history entry.
 *
 * ── The footer's four controls ──────────────────────────────────────────────
 * Previous and Next click the runtime's own buttons rather than calling into
 * it, so its gate, its scroll restoration and its own state updates all still
 * happen. **Mark complete** is the same `next` — the design's two words for one
 * act — and becomes **Finish** on the last lesson, which is the one place the
 * app leaves the course rather than moving inside it. Both are disabled while
 * the gate is closed: the runtime would refuse them anyway, and a button that
 * silently does nothing is worse than one that says it cannot.
 */

import { useRef, useState } from 'react';
import { learn } from '../api';
import { DeadlinePill } from '../components/Bits';
import { ErrorNote } from '../components/Chrome';
import { ds } from '../ds';
import type { BridgeLesson } from '../player/protocol';
import { useCourseBus } from '../player/bus';
import { useRouter } from '../router';
import { HAIR, LAYOUT, RADIUS, display, meta, monoPlain, plural, secondsLabel } from '../theme';
import { useLoad } from '../useLoad';

export default function Play({ id }: { id: string }) {
  const { Badge, Button, Icon, IconButton, Progress } = ds();
  const { navigate } = useRouter();
  const frame = useRef<HTMLIFrameElement | null>(null);

  const course = useLoad(() => learn.course(id), 'Could not load this course.', id);
  // `learn.play` is a POST that mints a fresh 12-hour ticket and lazily
  // creates the enrolment — not the harmless re-GET `useLoad` otherwise
  // assumes. `React.StrictMode` (main.tsx) runs a mount effect twice in
  // development, so without this guard every course open would mint two
  // tickets. The ref survives both invocations and hands the second one the
  // same in-flight promise instead of a second `POST /play`.
  const opened = useRef<{ id: string; promise: ReturnType<typeof learn.play> } | null>(null);
  const play = useLoad(
    () => {
      if (opened.current?.id !== id) opened.current = { id, promise: learn.play(id) };
      return opened.current.promise;
    },
    'Could not open this course.',
    id
  );
  const [leaving, setLeaving] = useState(false);

  const browse = play.data?.browse ?? false;
  const bus = useCourseBus({ frame, courseId: id, record: !!play.data && !browse });

  // `ready.lessons` is the rail once the frame is listening. Until then the
  // course's own lesson list stands in — the two agree, and an empty rail for
  // the seconds a course takes to load reads as a broken screen. Rail rows stay
  // disabled until `bus.ready`, so nothing can be clicked before the runtime's
  // own buttons exist either way.
  const lessons: BridgeLesson[] =
    bus.lessons.length > 0
      ? bus.lessons
      : (course.data?.lessons ?? []).map((l) => ({
          id: l.id,
          title: l.title,
          index: l.index,
          activities: l.activities,
          quiz: l.quiz
        }));
  const total = lessons.length;
  const index = bus.lessonIndex;
  const onLast = total > 0 && index === total - 1;
  const percent = bus.percent ?? course.data?.enrolment?.percent ?? 0;

  /** Every way out of the course: ask for a commit, post it, then go. */
  const leave = async (to: string) => {
    setLeaving(true);
    await bus.flush();
    navigate(to);
  };

  if (course.error || play.error) {
    return (
      <div style={{ padding: LAYOUT.contentPad, display: 'grid', gap: 14 }}>
        <ErrorNote>{course.error ?? play.error}</ErrorNote>
        <div>
          <Button variant="secondary" size="sm" onClick={() => navigate(`/course/${id}`)}>
            Back to the course
          </Button>
        </div>
      </div>
    );
  }

  const title = course.data?.course.title ?? '';
  const current = lessons[index] ?? null;
  const detail = course.data?.lessons[index] ?? null;

  return (
    <div
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--bg-page)'
      }}
    >
      <header
        style={{
          height: LAYOUT.topbar,
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          padding: '0 16px',
          background: 'var(--surface-card)',
          borderBottom: HAIR
        }}
      >
        <IconButton
          icon="X"
          label="Close the course"
          disabled={leaving}
          onClick={() => void leave(`/course/${id}`)}
        />

        <div style={{ minWidth: 0, display: 'grid', gap: 2 }}>
          <span
            style={{
              ...display(16),
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {title}
          </span>
          <span style={monoPlain}>
            {meta(
              total > 0 && index >= 0 ? `Lesson ${index + 1} of ${total}` : null,
              course.data?.assignedBy ? `assigned by ${course.data.assignedBy.name}` : null
            )}
          </span>
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 14 }}>
          {browse ? (
            <Badge tone="signal">Preview</Badge>
          ) : (
            <>
              <span className="mv-hide-narrow" style={{ width: 150 }}>
                <Progress value={percent} showValue size="sm" />
              </span>
              {course.data && (
                <DeadlinePill
                  badge={course.data.badge}
                  dueAt={course.data.dueAt}
                  daysLate={course.data.daysLate}
                />
              )}
            </>
          )}
          <Button
            variant="secondary"
            size="sm"
            disabled={leaving}
            onClick={() => void leave(`/course/${id}`)}
          >
            Save and close
          </Button>
        </div>
      </header>

      <ErrorNote>{bus.error}</ErrorNote>

      <div className="mv-play">
        <nav
          className="mv-play-rail"
          style={{
            borderRight: HAIR,
            background: 'var(--surface-card)',
            overflowY: 'auto',
            padding: '14px 10px',
            display: 'grid',
            gap: 3,
            alignContent: 'start'
          }}
        >
          {lessons.map((lesson, i) => {
            const state = course.data?.lessons.find((l) => l.id === lesson.id) ?? null;
            const done = !!state?.completedAt;
            const here = i === index;
            return (
              <button
                key={lesson.id}
                type="button"
                onClick={() => bus.send('open', lesson.id)}
                disabled={!bus.ready}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 9,
                  padding: '8px 10px',
                  border: 'none',
                  borderRadius: RADIUS.control,
                  textAlign: 'left',
                  background: here ? 'var(--primary-soft)' : 'transparent',
                  color: here ? 'var(--primary-text)' : 'var(--text-muted)',
                  fontSize: 13,
                  fontWeight: here ? 600 : 500
                }}
              >
                <Icon
                  name={done ? 'CheckCircle2' : here ? 'Play' : 'Circle'}
                  size={15}
                  color={done ? 'var(--success-600)' : 'currentColor'}
                />
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {lesson.title}
                </span>
                {lesson.quiz > 0 && <span style={monoPlain}>{`${lesson.quiz} q`}</span>}
              </button>
            );
          })}

          {course.data?.feedback.enabled && (
            <button
              type="button"
              disabled={!course.data.enrolment?.completedAt}
              onClick={() => void leave(`/course/${id}/done`)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 9,
                marginTop: 8,
                padding: '8px 10px',
                border: 'none',
                borderTop: HAIR,
                borderRadius: RADIUS.control,
                textAlign: 'left',
                background: 'transparent',
                color: 'var(--text-muted)',
                fontSize: 13
              }}
            >
              <Icon name="MessageSquare" size={15} />
              Feedback
            </button>
          )}
        </nav>

        <div style={{ minWidth: 0, minHeight: 0 }}>
          {play.data ? (
            <iframe
              ref={frame}
              className="mv-frame"
              title={title}
              src={play.data.url}
              sandbox="allow-scripts allow-forms allow-popups"
            />
          ) : (
            <p style={{ ...monoPlain, padding: 24 }}>Opening…</p>
          )}
        </div>
      </div>

      <footer
        style={{
          height: 58,
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '0 16px',
          background: 'var(--surface-card)',
          borderTop: HAIR
        }}
      >
        <Button
          variant="ghost"
          size="sm"
          disabled={!bus.ready || index <= 0}
          onClick={() => bus.send('prev')}
        >
          Previous lesson
        </Button>

        <span style={{ ...monoPlain, flex: 1, textAlign: 'center', minWidth: 0 }}>
          {current
            ? meta(
                `Lesson ${index + 1}`,
                current.title,
                detail ? secondsLabel(detail.plannedSeconds) : null
              )
            : total > 0
              ? plural(total, 'lesson')
              : ''}
        </span>

        <Button
          variant="secondary"
          size="sm"
          disabled={!bus.ready || !bus.gateOpen || leaving}
          onClick={() => {
            if (onLast) {
              bus.send('next');
              void leave(`/course/${id}/done`);
              return;
            }
            bus.send('next');
          }}
        >
          {onLast ? 'Finish' : 'Mark complete'}
        </Button>

        <Button
          variant="primary"
          size="sm"
          disabled={!bus.ready || !bus.gateOpen || onLast}
          onClick={() => bus.send('next')}
        >
          Next lesson
        </Button>
      </footer>
    </div>
  );
}
